import { Link, useLocation } from 'react-router-dom'
import { CalendarCheck, ChartColumn, UsersRound } from 'lucide-react'

/**
 * Configuración de elemento de navegación
 */
interface NavItem {
  label: string
  path: string
  icon: React.ReactNode
}

/**
 * Elementos de navegación para la barra lateral
 */
const navItems: NavItem[] = [
  { label: 'Accesos', path: '/accesos', icon: <UsersRound className="h-5 w-5" /> },
  { label: 'Informes', path: '/reports/leads', icon: <ChartColumn className="h-5 w-5" /> },
  ...(import.meta.env.VITE_RESERVATIONS_ENABLED === 'true' ? [{ label: 'Reservas y contratos', path: '/reservations', icon: <CalendarCheck className="h-5 w-5" /> }] : []),
  {
    label: 'Panel',
    path: '/dashboard',
    icon: <DashboardIcon className="h-5 w-5" />,
  },
  {
    label: 'Propiedades',
    path: '/properties',
    icon: <PropertiesIcon className="h-5 w-5" />,
  },
  {
    label: 'Edificios',
    path: '/buildings',
    icon: <BuildingsIcon className="h-5 w-5" />,
  },
  {
    label: 'Ordenar',
    path: '/viviendas',
    icon: <OrderIcon className="h-5 w-5" />,
  },
  {
    label: 'Contactos',
    path: '/contact',
    icon: <ContactIcon className="h-5 w-5" />,
  },
]

/**
 * Componente de barra lateral con enlaces de navegación.
 * Se muestra como barra lateral fija en escritorio y barra de navegación inferior en móvil.
 */
export function Sidebar() {
  const location = useLocation()

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-16 z-40 hidden h-[calc(100vh-4rem)] w-64 border-r bg-background md:block">
        <div className="flex h-full flex-col">
          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-4">
            {navItems.map((item) => {
              const isActive =
                location.pathname === item.path ||
                location.pathname.startsWith(item.path + '/')

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              )
            })}
          </nav>

          {/* Pie de Barra Lateral */}
          <div className="border-t p-4">
            <div className="rounded-lg bg-muted/50 p-3">
              <p className="text-xs font-medium text-muted-foreground">
                A1Homes Admin
              </p>
              <p className="text-xs text-muted-foreground/70">
                Sistema de Gestión de Propiedades
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:hidden">
        <div className="flex h-16 overflow-x-auto">
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + '/')

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex min-w-20 flex-shrink-0 flex-col items-center justify-center gap-1 px-2 transition-colors ${
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.icon}
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            )
          })}
        </div>
      </nav>
    </>
  )
}

/**
 * Componente de icono del panel
 */
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  )
}

/**
 * Componente de icono de propiedades/edificio
 */
function PropertiesIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  )
}

/**
 * Componente de icono de edificios (Building2 from lucide)
 */
function BuildingsIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </svg>
  )
}

/**
 * Componente de icono de contacto/correo
 */
function ContactIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  )
}

/**
 * Componente de icono de ordenar (ArrowUpDown from lucide)
 */
function OrderIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="m21 16-4 4-4-4" />
      <path d="M17 20V4" />
      <path d="m3 8 4-4 4 4" />
      <path d="M7 4v16" />
    </svg>
  )
}

export default Sidebar
